import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormState } from '../hooks/useFormState';
import { degreeInterestOptions, focusOptions, specializationOptions, getSpecializationsForFocus } from '../data/degrees';
interface HeroSectionProps {
  onSelectionChange?: (degreeInterest: string, areaOfStudy: string, specialization: string) => void;
}
export function HeroSection({
  onSelectionChange
}: HeroSectionProps) {
  const navigate = useNavigate();
  const {
    areaStudy,
    program
  } = useParams<{
    areaStudy?: string;
    program?: string;
  }>();
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateHeroForm
  } = useFormState();
  useEffect(() => {
    if (areaStudy && program && !formData.areaOfStudy) {
      updateMultiple({
        areaOfStudy: areaStudy,
        specialization: program
      });
    }
  }, [areaStudy, program, formData.areaOfStudy, updateMultiple]);
  const filteredSpecializations = formData.areaOfStudy ? getSpecializationsForFocus(formData.areaOfStudy) : [];
  const handleDegreeInterestChange = (value: string) => {
    updateFormData('degreeInterest', value);
    if (onSelectionChange) {
      onSelectionChange(value, formData.areaOfStudy, formData.specialization);
    }
  };
  const handleAreaOfStudyChange = (value: string) => {
    updateFormData('areaOfStudy', value);
    updateFormData('specialization', '');
    if (onSelectionChange) {
      onSelectionChange(formData.degreeInterest, value, '');
    }
  };
  const handleSpecializationChange = (value: string) => {
    updateFormData('specialization', value);
    if (onSelectionChange) {
      onSelectionChange(formData.degreeInterest, formData.areaOfStudy, value);
    }
  };
  const handleContinue = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateHeroForm() && onSelectionChange) {
      onSelectionChange(formData.degreeInterest, formData.areaOfStudy, formData.specialization);
    }
  };
  // Resolve display labels for trust signals
  const selectedDegree = degreeInterestOptions.find(d => d.value === formData.degreeInterest);
  const selectedFocus = focusOptions.find(f => f.value === formData.areaOfStudy);
  const selectedSpecOption = specializationOptions.find(s => s.value === formData.specialization);
  const selectedSpecName = selectedSpecOption?.type;
  // --- H1 headline logic ---
  const currentYear = new Date().getFullYear();
  const searchParams = new URLSearchParams(window.location.search);
  const t10verride = searchParams.get('t10verride');
  const smallWords = new Set(['for', 'in', 'of', 'a', 'an', 'the', 'and', '&']);
  function capitalizeHeadline(text: string): string {
    return text.split(' ').map((word, idx) => {
      if (idx === 0) return word.charAt(0).toUpperCase() + word.slice(1);
      if (smallWords.has(word.toLowerCase())) return word.toLowerCase();
      return word.charAt(0).toUpperCase() + word.slice(1);
    }).join(' ');
  }
  const degreeNameMap: Record<string, string> = {
    '1': 'Certificate',
    '2': "Associate's",
    '3': "Bachelor's",
    '4': "Master's"
  };
  const hasSpecificDegree = ['1', '2', '3', '4'].includes(formData.degreeInterest);
  const degreeName = degreeNameMap[formData.degreeInterest] || '';
  const isGeneralArea = formData.areaOfStudy === '9';
  const isGeneralProgram = !selectedSpecName || /^(General|All )/i.test(selectedSpecName);
  const areaName = selectedFocus?.type || '';
  function buildHeadline(): string {
    if (hasSpecificDegree) {
      if (!isGeneralProgram && selectedSpecName) {
        // Case 1: Specific degree + specific program
        return `Best Online ${degreeName} Degree in ${selectedSpecName} of ${currentYear}`;
      }
      if (isGeneralArea) {
        // Case 2: Specific degree + General area
        return `Best Colleges for a ${degreeName} Degree Program in ${currentYear}`;
      }
      // Case 3: Specific degree + specific area but general/all program
      return `Best Online ${degreeName} Degree in ${areaName} of ${currentYear}`;
    }
    // No specific degree selected
    if (isGeneralArea || !formData.areaOfStudy && !formData.specialization) {
      // Case 4a: default landing
      return `Top Accredited Degree Programs of ${currentYear}`;
    }
    if (isGeneralProgram || !selectedSpecName) {
      // Case 4b: No degree + specific area or "General" program
      return `Best Colleges for an Online ${areaName} Degree of ${currentYear}`;
    }
    // Case 4c: No degree + specific program
    return `Best Colleges for an Online ${selectedSpecName} Degree of ${currentYear}`;
  }
  let headline = capitalizeHeadline(buildHeadline());
  // Apply t10verride if present
  if (t10verride) {
    headline = headline.replace(/^Top Accredited/i, t10verride).replace(/^Best Online/i, t10verride).replace(/^Best Colleges for/i, t10verride);
  }
  return <div className="relative bg-navy overflow-hidden" data-id="element-215" data-mp="5pn89qs9n29q">
      {/* Abstract Background */}
      <div className="absolute inset-0 z-0" data-id="element-216" data-mp="5nakuy3qnzpa">
        <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-navy-light/30 blur-3xl" data-id="element-217" data-mp="1n0evlfy59af" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full bg-crimson/10 blur-3xl translate-x-1/4 translate-y-1/4" data-id="element-218" data-mp="fe57w4ot73bs" />
        <div className="absolute top-1/2 left-1/2 w-[300px] h-[300px] rounded-full bg-gold/5 blur-3xl -translate-x-1/2 -translate-y-1/2" data-id="element-219" data-mp="3iw6uxl2ukkmj" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-220" data-mp="3evwpc9iyvw87">
        <div className="py-12 md:py-16 lg:py-20 lg:flex lg:items-center lg:gap-12" data-id="element-221" data-mp="3sumnlshiw1y5">
          {/* Left Column: Copy */}
          <div className="lg:w-1/2 text-center lg:text-left mb-10 lg:mb-0" data-id="element-222" data-mp="3nui55wx20x5a">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-white/10 border border-white/15 text-sm font-semibold mb-5 text-[#B38B2E]" data-id="element-223" data-mp="183cezs2bx18n">
              <span className="flex h-2 w-2 rounded-full mr-2 animate-pulse bg-[#B38B2E]" data-id="element-224" data-mp="13759g0duhrr8" />
              Fall 2026 Enrollment Open
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[3.25rem] font-extrabold text-white tracking-tight mb-5 leading-[1.15]" data-id="element-225" data-mp="1f6k8k6yuo98v">
              {headline}
            </h1>

            <p className="text-base md:text-lg text-gray-300 mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed" data-id="element-226" data-mp="1p61duna6wl4b">
              Compare accredited online colleges and universities. Get matched
              with programs that fit your career goals, schedule, and budget.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 sm:gap-6 text-sm text-gray-300" data-id="element-227" data-mp="11fx92cl9mfbq">
              <div className="flex items-center" data-id="element-228" data-mp="167p1ytrw8x51">
                <svg className="w-5 h-5 text-green-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-229" data-mp="8r9lgad1ndv0">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" data-id="element-230" data-mp="67d9fvt1c4ms" />
                </svg>
                Flexible and Convenient
              </div>
              <div className="flex items-center" data-id="element-231" data-mp="3neyw495cy32m">
                <svg className="w-5 h-5 text-green-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-232" data-mp="3ne5pd86efdkh">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" data-id="element-233" data-mp="3elc0qk52b1ty" />
                </svg>
                Accredited Schools
              </div>
              <div className="flex items-center" data-id="element-234" data-mp="2zo30f0xs6nq3">
                <svg className="w-5 h-5 text-green-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-235" data-mp="2vo15damjwsjs">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" data-id="element-236" data-mp="sefora442baw" />
                </svg>
                No Obligation
              </div>
            </div>
          </div>

          {/* Right Column: Form Card */}
          <div className="lg:w-1/2 flex justify-center lg:justify-end" data-id="element-237" data-mp="4micxj0nvj9s">
            <div className="w-full max-w-md" data-id="element-238" data-mp="9g3efl1gqvdq">
              <form onSubmit={handleContinue} className="bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-100" data-id="element-239" data-mp="3qvtfpqf2clkk">
                {/* Card Header */}
                <div className="px-6 py-3.5 bg-[#A52A2A]" data-id="element-240" data-mp="3hvd9waaul2cj">
                  <h2 className="text-white font-bold text-center text-sm tracking-widest uppercase" data-id="element-241" data-mp="3hvwslt8bnurm">
                    Search Featured Schools
                  </h2>
                </div>

                <div className="p-6 space-y-4" data-id="element-242" data-mp="338ofunzd7hdj">
                  {/* Degree Level */}
                  <div data-id="element-243" data-mp="2yawc8wpcy6ti">
                    <select id="degreeInterest" value={formData.degreeInterest} onChange={e => handleDegreeInterestChange(e.target.value)} aria-label="Select Degree Level" className={`block w-full px-3 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${errors.degreeInterest ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-244" data-mp="2ybejsphh07gq">
                      <option value="" data-id="element-245" data-mp="3r03lapmfuet0">Select Degree Level</option>
                      {degreeInterestOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-246" data-mp="3tjcl1mls28b">
                          {opt.type}
                        </option>)}
                    </select>
                    {errors.degreeInterest && <p className="mt-1 text-xs text-red-600" data-id="element-247" data-mp="2m13df7u1ya6x">
                        {errors.degreeInterest}
                      </p>}
                  </div>

                  {/* Area of Study */}
                  <div data-id="element-248" data-mp="2d31gb3dfw1ox">
                    <select id="areaOfStudy" value={formData.areaOfStudy} onChange={e => handleAreaOfStudyChange(e.target.value)} aria-label="Select Area of Study" className={`block w-full px-3 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${errors.areaOfStudy ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-249" data-mp="39rh6n1odif33">
                      <option value="" data-id="element-250" data-mp="1kbhs151zjxg2">Select Area of Study</option>
                      {focusOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-251" data-mp="1aa5ojwc4kie0">
                          {opt.type}
                        </option>)}
                    </select>
                    {errors.areaOfStudy && <p className="mt-1 text-xs text-red-600" data-id="element-252" data-mp="1k4xp99107ld0">
                        {errors.areaOfStudy}
                      </p>}
                  </div>

                  {/* Specialization */}
                  <div data-id="element-253" data-mp="1xxnle6a8y6nu">
                    <select id="specialization" value={formData.specialization} onChange={e => handleSpecializationChange(e.target.value)} disabled={!formData.areaOfStudy} aria-label="Select Program" className={`block w-full px-3 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed ${errors.specialization ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-254" data-mp="1xx5iiqt2wu6y">
                      <option value="" data-id="element-255" data-mp="fhekui1cast7">
                        {formData.areaOfStudy ? 'Select Program' : 'Select an area of study first'}
                      </option>
                      {filteredSpecializations.map(opt => <option key={opt.value} value={opt.value} data-id="element-256" data-mp="jgnelmwdqqor">
                          {opt.type}
                        </option>)}
                    </select>
                    {errors.specialization && <p className="mt-1 text-xs text-red-600" data-id="element-257" data-mp="t9kqvbas3jex">
                        {errors.specialization}
                      </p>}
                  </div>

                  {/* Continue Button */}
                  <button type="submit" className="w-full hover:bg-navy-light text-white font-bold py-3.5 px-6 rounded-md text-sm tracking-wider uppercase transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy bg-[#B38B2E]" data-id="element-258" data-mp="1ltyc0n6x97rf">
                    Search Programs
                  </button>
                </div>
              </form>

              {/* Social proof */}
              <p className="text-center text-gray-300 text-sm mt-4 italic" data-id="element-259" data-mp="13144uffjijxr">
                Used by{' '}
                <strong className="text-white font-semibold" data-id="element-260" data-mp="17vrwgn5ubugk">
                  2M+ students
                </strong>{' '}
                to choose the right school.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}